// src/App.js
import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import * as Sentry from "@sentry/react";
import FirebaseIdentitySync from './components/FirebaseIdentitySync';
import { fetchSiteContent } from './services/firebase';
import { applyThemeFromSiteContent } from './styles/theme';
import SplashScreen from './components/SplashScreen';

import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Programs from './pages/Programs';
import ProgramView from './pages/ProgramView';
import FreeWorkouts from './pages/FreeWorkouts';
import AdminDashboard from './pages/AdminDashboard';
import Account from './pages/Account';
import AboutMe from './pages/AboutMe';
import Workouts from './pages/Workouts';
import MilestonesPage from './pages/MilestonesPage';
import TermsPage from './pages/TermsPage';
import JoinPage from './pages/JoinPage';
import ClassPage from './pages/ClassPage';
import PublicSeriesPage from './pages/PublicSeriesPage';

import WelcomePage from './pages/WelcomePage';

function Loader() {
    return (
        <div style={{
            minHeight: '100vh',
            background: 'var(--mom-cream)',
            filter: 'blur(8px)',
            opacity: 0.6,
            transition: 'filter 0.3s ease, opacity 0.3s ease',
            pointerEvents: 'none',
        }}>
            <div style={{ height: 70, background: 'var(--mom-glass-bg)' }} />
            <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}>
                <div className="mom-skeleton" style={{ height: 200, borderRadius: 28, marginBottom: 24 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div className="mom-skeleton" style={{ height: 260, borderRadius: 22 }} />
                    <div className="mom-skeleton" style={{ height: 260, borderRadius: 22 }} />
                </div>
            </div>
        </div>
    );
}

// ── Clerk status banner — detects Clerk SDK failure globally ──
// Uses window.Clerk (set by Clerk's SDK on load) instead of useUser(),
// so it works on ALL pages including landing and welcome — even for
// unauthenticated users who haven't interacted with Clerk yet.
function ClerkStatusBanner() {
    const [show, setShow] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        // Check if Clerk SDK has loaded after 15 seconds
        // Normal load time is 1-2 seconds, so 15s means something is genuinely wrong
        const timer = setTimeout(() => {
            const clerkLoaded = window.Clerk && window.Clerk.loaded;
            if (!clerkLoaded) setShow(true);
        }, 15000);

        // Also set up a check that hides the banner if Clerk recovers
        const recovery = setInterval(() => {
            if (window.Clerk && window.Clerk.loaded) {
                setShow(false);
                clearInterval(recovery);
            }
        }, 5000);

        return () => { clearTimeout(timer); clearInterval(recovery); };
    }, []);

    if (!show || dismissed) return null;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
            background: 'linear-gradient(135deg, #2d2926, #3d3632)',
            padding: '12px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
        }}>
            <p style={{
                fontFamily: 'var(--mom-font-sans, -apple-system, sans-serif)',
                fontSize: 13, color: 'rgba(255,255,255,0.9)', lineHeight: 1.4,
                margin: 0, flex: 1, textAlign: 'center',
            }}>
                Some features are temporarily unavailable — we're working on it. Please try again in a few minutes.
            </p>
            <button onClick={() => setDismissed(true)} style={{
                background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
                fontSize: 18, cursor: 'pointer', padding: '0 4px', lineHeight: 1, flexShrink: 0,
            }} aria-label="Dismiss">×</button>
        </div>
    );
}

function App({ demoMode = false }) {
    // Apply theme colors + fonts from siteConfig.
    //
    // Note: `index.js` already calls `applyThemeFromSiteContent()` SYNCHRONOUSLY
    // before this component mounts, seeded from `getCachedSiteContent()`. So for
    // returning visitors, brand colors are correct on first paint and this
    // useEffect's job is purely revalidation — it picks up any admin edits that
    // landed since the localStorage cache was last written.
    //
    // First-ever visitors (no cache yet) rely on this effect for theme — they'll
    // see a brief flash of default brand colors before this resolves. The cache
    // populates after this first fetch so subsequent visits are flash-free.
    useEffect(() => {
        fetchSiteContent().then(applyThemeFromSiteContent);
    }, []);

    // Splash screen — once per session
    //
    // Note (2026-05-15): A /join-only skip was briefly trialled here and
    // reverted same-day. User preference is splash-on for all routes
    // including /join — the splash effectively masks the underlying page
    // load behind the brand moment, which feels intentional rather than
    // slow. The localStorage-backed siteConfig cache (PERF-003) handles
    // the underlying speed so the page is fully ready behind the splash
    // by the time it fades out.
    const [showSplash, setShowSplash] = useState(() => {
        try {
            return !sessionStorage.getItem('mom_splash_shown');
        } catch { return true; }
    });
    const handleSplashComplete = () => {
        setShowSplash(false);
        try { sessionStorage.setItem('mom_splash_shown', '1'); } catch { }
    };

    if (demoMode) return <DemoApp />;

    return (
        <Sentry.ErrorBoundary
            fallback={
                <div style={{
                    display: "flex", justifyContent: "center", alignItems: "center",
                    height: "100vh", fontFamily: "var(--mom-font-sans)",
                    textAlign: "center", padding: "20px",
                    background: "var(--mom-cream, #faf6f1)"
                }}>
                    <div>
                        <h2 style={{
                            fontFamily: "var(--mom-font-serif)",
                            fontStyle: "italic",
                            color: "var(--mom-charcoal, #2d2926)"
                        }}>
                            Something went wrong
                        </h2>
                        <p style={{ color: "var(--mom-warm-gray, #8a837d)" }}>
                            We've been notified and are looking into it.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                background: "linear-gradient(135deg, #f0d6d4, #e8bfbc)",
                                border: "none", borderRadius: "14px",
                                padding: "12px 32px", fontWeight: 600,
                                cursor: "pointer", fontFamily: "var(--mom-font-sans)",
                                color: "var(--mom-charcoal, #2d2926)"
                            }}
                        >
                            Refresh Page
                        </button>
                    </div>
                </div>
            }
        >
            <>
                {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
                <ClerkStatusBanner />
                <PostAuthRedirect />
                {/* Invisible: hands the Clerk login to Firestore in the background
                    (adr-firestore-identity.md a2). Renders nothing. */}
                <FirebaseIdentitySync />
                <div className="mom-atmosphere" />
                {/* Express consent is captured inline on Clerk's signup form
                    (Dashboard → User & authentication → Legal → "Require express
                    consent"). The audit trail is mirrored to Firestore via
                    `api/clerk-webhook.js` on `user.created`. The legacy
                    WaiverGate / WaiverModal flow was retired 2026-05-05 — see
                    `docs/decisions/adr-waiver-clerk-express-consent.md`. The
                    `/terms` route below is the public legal page Clerk's
                    signup form deep-links to. */}
                <Routes>
                    <Route path="/" element={<ClerkLandingRoute />} />
                    <Route path="/landing" element={<ClerkLandingRoute />} />
                    <Route path="/dashboard" element={<ClerkProtected><Dashboard /></ClerkProtected>} />
                    <Route path="/programs" element={<PublicOrAuth><Programs /></PublicOrAuth>} />
                    <Route path="/program/:programId" element={<PublicOrAuth><ProgramView /></PublicOrAuth>} />
                    <Route path="/free-workouts" element={<ClerkProtected><FreeWorkouts /></ClerkProtected>} />
                    <Route path="/account" element={<ClerkProtected><Account /></ClerkProtected>} />
                    <Route path="/about" element={<AboutMe />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/join" element={<JoinPage />} />
                    <Route path="/class/:id" element={<ClassPage />} />
                    <Route path="/welcome" element={<WelcomePage />} />
                    <Route path="/live" element={<Navigate to="/workouts" replace />} />
                    <Route path="/workouts" element={<WorkoutsRoute />} />
                    <Route path="/milestones" element={<ClerkProtected><MilestonesPage /></ClerkProtected>} />
                    <Route path="/admin" element={<ClerkAdminProtected><AdminDashboard /></ClerkAdminProtected>} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </>
        </Sentry.ErrorBoundary>
    );
}

function DemoApp() {
    const [showSplash, setShowSplash] = useState(() => {
        try { return !sessionStorage.getItem('mom_splash_shown'); } catch { return true; }
    });
    const handleSplashComplete = () => {
        setShowSplash(false);
        try { sessionStorage.setItem('mom_splash_shown', '1'); } catch { }
    };

    return (
        <>
            {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
            <div className="mom-atmosphere" />
            <Routes>
                <Route path="/" element={<LandingPage demoMode />} />
                <Route path="/landing" element={<LandingPage demoMode />} />
                <Route path="/dashboard" element={<Dashboard demoMode />} />
                <Route path="/programs" element={<Programs demoMode />} />
                <Route path="/program/:programId" element={<ProgramView demoMode />} />
                <Route path="/free-workouts" element={<FreeWorkouts demoMode />} />
                <Route path="/account" element={<Account demoMode />} />
                <Route path="/about" element={<AboutMe demoMode />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/join" element={<JoinPage demoMode />} />
                <Route path="/class/:id" element={<ClassPage demoMode />} />
                <Route path="/live" element={<Navigate to="/workouts" replace />} />
                <Route path="/workouts" element={<Workouts demoMode />} />
                <Route path="/admin" element={<AdminDashboard demoMode />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </>
    );
}

function ClerkLandingRoute() {
    const { isLoaded, isSignedIn } = useUser();
    const navigate = useNavigate();
    const stay = window.location.pathname === '/landing' || new URLSearchParams(window.location.search).get('stayOnLandingPage') === 'true';
    // Read at render time (NOT inside the effect): on the commit where the member
    // becomes signed-in, the deep-link stash is still present because PostAuthRedirect
    // clears it in its own post-render effect. Capturing it here guarantees we defer
    // the /dashboard redirect for that commit and hand off cleanly to PostAuthRedirect.
    const deferForPostAuth = hasFreshPostAuthDest();
    useEffect(() => {
        if (isLoaded && isSignedIn && !stay && !deferForPostAuth) navigate('/dashboard');
    }, [isLoaded, isSignedIn, navigate, stay, deferForPostAuth]);
    if (!isLoaded) return <Loader />;
    return <LandingPage />;
}

// Key under which a logged-out visitor's intended destination is stashed so it
// survives the sign-in round-trip (modal sign-in lands back on '/'). Restored by
// PostAuthRedirect. TTL-guarded so a stale stash can never hijack a later sign-in.
const POST_AUTH_DEST_KEY = 'mom_post_auth_dest';
const POST_AUTH_DEST_AT_KEY = 'mom_post_auth_dest_at';
const POST_AUTH_TTL_MS = 5 * 60 * 1000;

// True when a deep-link intent was stashed (by ClerkProtected) within the TTL.
// ClerkLandingRoute uses this to step aside so PostAuthRedirect can deliver the
// member to their intended destination instead of the default /dashboard.
function hasFreshPostAuthDest() {
    try {
        const dest = sessionStorage.getItem(POST_AUTH_DEST_KEY);
        const at = Number(sessionStorage.getItem(POST_AUTH_DEST_AT_KEY) || 0);
        return !!dest && !!at && (Date.now() - at <= POST_AUTH_TTL_MS);
    } catch (_) { return false; }
}

// /workouts serves TWO audiences from ONE address.
//
// A challenge is addressable at /workouts?series=<slug-or-id>, and those links
// are already in circulation — so when the owner asked to make a challenge
// openable without an account, the requirement was to reuse that link rather
// than mint a second address. This resolver is how one URL does both jobs.
//
// Four cases, and only the last is new:
//   signed in                            -> today's page, byte-identical
//   signed out, no ?series=              -> today's page (sign-in bounce)
//   signed out, ?series=, NOT public     -> today's page (stash -> sign in -> return)
//   signed out, ?series=, public+active  -> the public challenge page
//
// The anonymous case NEVER renders Workouts: that component owns the live-class
// path and is ~6,700 lines, and putting an unauthenticated code path through it
// is precisely the blast radius this design exists to avoid.
// See docs/decisions/adr-public-series-access.md.
function WorkoutsRoute() {
    const { isLoaded, isSignedIn } = useUser();
    const location = useLocation();
    // Set when the token turns out NOT to be a public challenge. Falling back to
    // ClerkProtected reproduces today's private-link behaviour exactly: it
    // stashes the destination, sends them to sign in, and PostAuthRedirect
    // returns them to this same URL afterwards.
    const [notPublic, setNotPublic] = useState(false);
    const seriesToken = React.useMemo(() => {
        try { return (new URLSearchParams(location.search).get('series') || '').trim(); }
        catch (_) { return ''; }
    }, [location.search]);

    // A different token deserves a fresh verdict — otherwise one private link
    // would poison every challenge link opened after it in the same session.
    useEffect(() => { setNotPublic(false); }, [seriesToken]);

    // Stable identity so PublicSeriesPage's resolve effect can't re-fire on it.
    const handleNotPublic = useCallback(() => setNotPublic(true), []);

    if (!isLoaded) return <Loader />;
    if (!isSignedIn && seriesToken && !notPublic) {
        return <PublicSeriesPage token={seriesToken} onNotPublic={handleNotPublic} />;
    }
    return <ClerkProtected><Workouts /></ClerkProtected>;
}

function ClerkProtected({ children }) {
    const { isLoaded, isSignedIn } = useUser();
    const navigate = useNavigate();
    useEffect(() => {
        if (isLoaded && !isSignedIn) {
            // Preserve the intended path + query (e.g. /workouts?tab=series from an
            // external marketing link) before bouncing to the landing page. After the
            // member signs in, PostAuthRedirect sends them on to this destination.
            // Purely additive: when nothing meaningful is stashed, the restore is a
            // no-op and the flow is byte-identical to the prior navigate('/').
            try {
                const dest = window.location.pathname + window.location.search;
                if (dest && dest !== '/' && dest !== '/landing') {
                    sessionStorage.setItem(POST_AUTH_DEST_KEY, dest);
                    sessionStorage.setItem(POST_AUTH_DEST_AT_KEY, String(Date.now()));
                }
            } catch (_) { /* sessionStorage unavailable — fall through to plain redirect */ }
            navigate('/');
        }
    }, [isLoaded, isSignedIn, navigate]);
    if (!isLoaded || !isSignedIn) return <Loader />;
    return children;
}

// Restores a deep-link intent captured by ClerkProtected once the member is
// signed in. Mounted alongside <Routes> so it sees every location. Strictly
// one-shot + TTL-guarded: it clears the stash the moment it reads it (so it can
// never loop), ignores anything older than the TTL, and only navigates when the
// signed-in user isn't already at the destination.
function PostAuthRedirect() {
    const { isLoaded, isSignedIn } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    useEffect(() => {
        if (!isLoaded || !isSignedIn) return;
        let dest = null; let at = 0;
        try {
            dest = sessionStorage.getItem(POST_AUTH_DEST_KEY);
            at = Number(sessionStorage.getItem(POST_AUTH_DEST_AT_KEY) || 0);
        } catch (_) { return; }
        if (!dest) return;
        // Clear FIRST so this can only ever fire once — no redirect loop is possible.
        try {
            sessionStorage.removeItem(POST_AUTH_DEST_KEY);
            sessionStorage.removeItem(POST_AUTH_DEST_AT_KEY);
        } catch (_) { /* ignore */ }
        if (!at || Date.now() - at > POST_AUTH_TTL_MS) return; // stale → ignore
        const current = location.pathname + location.search;
        if (dest !== current) navigate(dest, { replace: true });
    }, [isLoaded, isSignedIn, navigate, location.pathname, location.search]);
    return null;
}

function PublicOrAuth({ children }) {
    const { isLoaded } = useUser();
    if (!isLoaded) return <Loader />;
    return children;
}

function ClerkAdminProtected({ children }) {
    const { isLoaded, isSignedIn, user } = useUser();
    const navigate = useNavigate();
    const isAdmin = user?.publicMetadata?.role === 'admin';
    useEffect(() => {
        if (isLoaded && (!isSignedIn || !isAdmin)) navigate('/');
    }, [isLoaded, isSignedIn, isAdmin, navigate]);
    if (!isLoaded || !isSignedIn || !isAdmin) return <Loader />;
    return children;
}

function ClerkFriendProtected({ children }) {
    const { isLoaded, isSignedIn, user } = useUser();
    const navigate = useNavigate();
    const role = user?.publicMetadata?.role;
    const hasAccess = role === 'admin' || role === 'friend';
    useEffect(() => {
        if (isLoaded && (!isSignedIn || !hasAccess)) navigate('/');
    }, [isLoaded, isSignedIn, hasAccess, navigate]);
    if (!isLoaded || !isSignedIn || !hasAccess) return <Loader />;
    return children;
}

export default App;
